import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Button from 'flarum/common/components/Button';
import { EVENTS_TAG_ID, EVENTS_PARENT_TAG_ID } from '../utils/eventsTag';
const LIMIT = 10;
// DD.MM.YY - matches champis.net/home's own compact date format for this
// widget specifically (its discussion-hero equivalent, addEventDetails.js,
// uses a 4-digit year instead - deliberately different, this one mirrors
// the reference page exactly rather than the rest of this app's own style).
function formatDate(isoDate) {
  const [y, m, day] = isoDate.split('-');
  return `${day}.${m}.${y.slice(2)}`;
}

/**
 * Homepage sidebar widget, at the very bottom (see addHomeEvents.js) -
 * "Prochains événements" on champis.net/home: every discussion with event
 * data that hasn't ended yet, soonest-starting first, each showing its
 * date range and title and linking to the discussion itself.
 */
export default class UpcomingEvents extends Component {
  items = null;
  oninit(vnode) {
    super.oninit(vnode);
    this.items = null;
    this.load();
  }
  load() {
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/upcoming-events',
      params: {
        filter: {
          limit: LIMIT
        }
      }
    }).then(response => {
      this.items = (response.data || []).map(item => ({
        id: item.id,
        ...item.attributes
      }));
      m.redraw();
    }).catch(() => {
      this.items = [];
      m.redraw();
    });
  }
  view() {
    return m("div", {
      className: "ChampisUpcomingEvents"
    }, m("h2", {
      className: "ChampisSpeciesSearch-label"
    }, m("span", null, m("i", {
      className: "icon fas fa-calendar-alt",
      "aria-hidden": "true"
    }), " ", app.translator.trans('champis-events.forum.upcoming_events.title')), this.newEventButton()), this.items === null ? m(LoadingIndicator, {
      display: "block"
    }) : this.items.length > 0 && m("ul", null, this.items.map(item => this.row(item))));
  }

  // Same disabled/login-fallback logic (and the same reason for a plain
  // copy rather than reusing IndexSidebar's own newDiscussionAction) as
  // champis-species' DescriptionsPage.js newDiscussionButton() - except
  // this one also pre-selects the events tag (plus its required primary
  // parent tag, same idiom as flarum-tags' own addTagComposer.js) so
  // starting a new event doesn't require picking the tag by hand.
  newEventButton() {
    const canStartDiscussion = app.forum.attribute('canStartDiscussion') || !app.session.user;
    return m(Button, {
      icon: "fas fa-plus",
      className: "Button Button--icon Button--flat ChampisUpcomingEvents-addButton",
      "aria-label": app.translator.trans('champis-events.forum.upcoming_events.new_event_button'),
      onclick: () => this.newEventAction(),
      disabled: !canStartDiscussion
    });
  }
  newEventAction() {
    if (!app.session.user) {
      app.modal.show(() => flarum.reg.asyncModuleImport('flarum/forum/components/LogInModal'));
      return;
    }

    // The tags first, set before the composer shows: a composer without a
    // category opens the category picker at once (see champis-forum's
    // chooseCategoryOnNewDiscussion.ts). Usually already in the store.
    const tag = id => app.store.getById('tags', String(id)) ?? app.store.find('tags', String(id));
    Promise.all([tag(EVENTS_PARENT_TAG_ID), tag(EVENTS_TAG_ID)]).then(_ref => {
      let [parentTag, eventsTag] = _ref;
      return app.composer.load(() => flarum.reg.asyncModuleImport('flarum/forum/components/DiscussionComposer'), {
        user: app.session.user
      }).then(loaded => {
        // Not over a draft its author chose to keep (see champis-forum's
        // startDiscussion()).
        if (!loaded) return;
        app.composer.fields.tags = [parentTag, eventsTag];
        app.composer.show();
      });
    });
  }

  // Same two-line row shape as champis-species' /descriptions listing
  // (DescriptionsPage.js: bold title link, muted secondary line below) -
  // here the secondary line is the event's location instead of an author.
  // Within the first line, only the title is bold - the date is a <small>
  // (not just a smaller font-size class) for the same reason
  // .ChampisDiscussionRow-time is one: that's what actually makes it
  // render smaller than its sibling text, browsers shrink <small> content
  // by default (roughly 83%) - a plain span at the row's own 14px
  // wouldn't visually match the discussion list's date at all.
  //
  // The Link itself is the whole row (className moved onto it, not a <li>
  // wrapping a narrower inner link) - no separate icon-link to carve out
  // space for here, unlike DescriptionsPage.js, so the simple version of
  // "the whole block is clickable" is just "make the link the block".
  row(item) {
    return m("li", {
      key: item.discussionId
    }, m(Link, {
      href: app.route('discussion', {
        id: item.discussionId
      }),
      className: "ChampisUpcomingEvents-item"
    }, m("small", {
      className: "ChampisUpcomingEvents-date"
    }, formatDate(item.startDate), item.endDate && item.endDate !== item.startDate ? ' - ' + formatDate(item.endDate) : ''), ' - ', m("span", {
      className: "ChampisUpcomingEvents-title"
    }, item.title), item.location && m("div", {
      className: "ChampisUpcomingEvents-location"
    }, item.location)));
  }
}
flarum.reg.add('champis-events', 'forum/components/UpcomingEvents', UpcomingEvents);